import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Pull down at the top of a page to refresh it, as in a native app: a small spinner drops out from
// under the header, turns as you pull, and once past the line reloads the page through Turbo.
//
// Only in the app added to the home screen. A phone's browser has its own pull to refresh in a tab,
// and the native app shell brings its own too, so there it stays out of the way. It also leaves alone
// the map, open sheets and dialogs, sideways swipes, and forms you've started filling in.
const THRESHOLD = 72
const MAX_PULL = 112
const RESISTANCE = 0.5

export default class extends Controller {
  connect() {
    if (!this.enabled) return

    this.onTouchStart = this.onTouchStart.bind(this)
    this.onTouchMove = this.onTouchMove.bind(this)
    this.onTouchEnd = this.onTouchEnd.bind(this)
    addEventListener("touchstart", this.onTouchStart, { passive: true })
    addEventListener("touchmove", this.onTouchMove, { passive: false })
    addEventListener("touchend", this.onTouchEnd)
    addEventListener("touchcancel", this.onTouchEnd)
  }

  disconnect() {
    removeEventListener("touchstart", this.onTouchStart)
    removeEventListener("touchmove", this.onTouchMove)
    removeEventListener("touchend", this.onTouchEnd)
    removeEventListener("touchcancel", this.onTouchEnd)
    this.indicator?.remove()
  }

  get enabled() {
    const standalone = matchMedia("(display-mode: standalone)").matches || navigator.standalone === true
    return standalone && !/Hotwire Native|Turbo Native/.test(navigator.userAgent)
  }

  onTouchStart(event) {
    this.pull = null
    if (this.refreshing || event.touches.length > 1 || window.scrollY > 0 || this.blocked(event.target)) return

    const touch = event.touches[0]
    this.pull = { startX: touch.clientX, startY: touch.clientY, distance: 0, decided: false }
  }

  onTouchMove(event) {
    const pull = this.pull
    if (!pull) return

    const touch = event.touches[0]
    const dx = touch.clientX - pull.startX
    const dy = touch.clientY - pull.startY

    // The first few pixels decide: a pull down at the top, or anything else (scrolling, a sideways swipe)
    if (!pull.decided) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return
      if (dy <= 0 || Math.abs(dx) > Math.abs(dy) || window.scrollY > 0) return void (this.pull = null)
      pull.decided = true
      this.showIndicator()
    }

    event.preventDefault()
    pull.distance = Math.min(MAX_PULL, Math.max(0, dy * RESISTANCE))
    this.place(pull.distance)
  }

  onTouchEnd() {
    const pull = this.pull
    this.pull = null
    if (!pull?.decided) return

    if (pull.distance >= THRESHOLD) {
      this.refresh()
    } else {
      this.retract()
    }
  }

  blocked(target) {
    if (document.body.style.overflow === "hidden" || document.querySelector("dialog[open]")) return true
    if (target.closest?.(".map-surface, .sheet-content, .slide-menu-content, [data-pull-refresh=off]")) return true

    // Don't throw away something being typed
    return [...document.querySelectorAll("input:not([type=hidden]):not([type=search]), textarea")]
      .some(field => field.value && field.value !== field.defaultValue)
  }

  showIndicator() {
    if (!this.indicator) {
      this.indicator = document.createElement("div")
      this.indicator.className = "pull-refresh"
      this.indicator.setAttribute("aria-hidden", "true")
      this.indicator.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round"><path d="M12 3a9 9 0 1 1-8.2 5.3" /><path d="M3.5 3.5v5h5" stroke-linejoin="round" /></svg>'
      document.body.appendChild(this.indicator)
    }

    const header = document.querySelector(".app-header")
    this.indicator.style.top = `${header ? header.getBoundingClientRect().bottom : 0}px`
    this.indicator.classList.remove("is-settling", "is-refreshing")
  }

  place(distance) {
    const progress = Math.min(1, distance / THRESHOLD)
    this.indicator.style.setProperty("--pull", `${distance}px`)
    this.indicator.style.setProperty("--pull-progress", progress)
    this.indicator.classList.toggle("is-ready", progress >= 1)
  }

  retract() {
    this.indicator.classList.add("is-settling")
    this.place(0)
  }

  refresh() {
    this.refreshing = true
    navigator.vibrate?.(8)
    this.indicator.classList.add("is-settling", "is-refreshing")
    this.place(THRESHOLD * 0.75)
    Turbo.visit(window.location.href, { action: "replace" })
  }
}
